import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { getAdminContent, adminLogout } from '@/utils/api'

interface AuthContextValue {
  sessionToken: string | null
  isChecking: boolean
  isAuthenticated: boolean
  login: (token: string) => void
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [sessionToken, setSessionToken] = useState<string | null>(null)
  const [isChecking, setIsChecking] = useState(true)

  useEffect(() => {
    let cancelled = false

    const token = localStorage.getItem('admin_session_token')
    if (token) {
      setSessionToken(token)
      getAdminContent()
        .then(() => {
          if (!cancelled) setIsChecking(false)
        })
        .catch(() => {
          if (!cancelled) {
            setSessionToken(null)
            localStorage.removeItem('admin_session_token')
            setIsChecking(false)
          }
        })
    } else {
      setIsChecking(false)
    }

    return () => {
      cancelled = true
    }
  }, [])

  const login = (token: string) => {
    setSessionToken(token)
    localStorage.setItem('admin_session_token', token)
  }

  const logout = async () => {
    try {
      await adminLogout()
    } catch {
      // ignore backend logout errors, still clear local state
    } finally {
      setSessionToken(null)
      localStorage.removeItem('admin_session_token')
    }
  }

  const isAuthenticated = !!sessionToken

  return (
    <AuthContext.Provider value={{ sessionToken, isChecking, isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider')
  }
  return context
}
